export type ComplexityLevel = "simple" | "medium" | "detailed";
export type TattooStyle =
  | "black_grey"
  | "full_color"
  | "realism"
  | "fine_line"
  | "traditional"
  | "neo_traditional"
  | "watercolor"
  | "tribal"
  | "japanese"
  | "geometric"
  | "lettering"
  | "script"
  | "dotwork"
  | "mandala"
  | "blackwork"
  | "illustrative"
  | "surrealism"
  | "ornamental"
  | "micro_realism"
  | "trash_polka"
  | "other";
export type BodyPart =
  | "arm"
  | "forearm"
  | "upper_arm"
  | "leg"
  | "thigh"
  | "calf"
  | "back"
  | "chest"
  | "ribs"
  | "stomach"
  | "hand"
  | "foot"
  | "neck"
  | "face"
  | "shoulder"
  | "hip"
  | "finger"
  | "other";
export type SizeCategory = "mini" | "small" | "medium" | "large" | "xl";

export interface ThemeSettings {
  mode: "dark" | "light";
  background_type: "solid" | "image";
  background_color: string;
  background_image_url: string;
  border_color?: string;
  glow_color?: string;
  font_family?: string;
}

export const DEFAULT_THEME_V2: ThemeSettings = {
  mode: "dark",
  background_type: "solid",
  background_color: "#0a0a0a",
  background_image_url: "",
  border_color: "#D4AF37",
  glow_color: "#D4AF3730",
  font_family: "Playfair Display",
};

export interface InstagramMediaItem {
  id:             string;
  media_type:     "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
  media_url?:     string;
  thumbnail_url?: string;
  permalink:      string;
  caption?:       string;
  timestamp:      string;
}

export interface ArtistProfile {
  id: string;
  user_id: string;
  studio_name: string | null;
  artist_name: string;
  experience_level: string | null;
  location: string | null;
  slug: string | null;
  calculator_code: string | null;
  theme_primary_hue: number | null;
  theme_primary_saturation: number | null;
  theme_primary_lightness: number | null;
  theme_settings: ThemeSettings | null;
  theme_version: number | null;
  logo_url: string | null;
  currency: string;
  language: string;
  subscription_tier: string;
  consultation_email: string | null;
  booking_url: string | null;
  onboarding_completed: boolean;
  is_test_account: boolean;
  studio_id: string | null;
  street_address: string | null;
  postal_code: string | null;
  city: string | null;
  // --- Nieuwe Velden voor Wereldkaart ---
  latitude: number | null;
  longitude: number | null;
  country: string | null;
  province: string | null;
  // --------------------------------------
  phone: string | null;
  website: string | null;
  instagram: string | null;
  created_at: string;
  updated_at: string;
  style_thumbnails: Record<string, string> | null;
  portfolio_images: string[] | null;
  premium_status: string | null;
  is_verified: boolean;
  google_rating: number | null;
  google_review_count: number | null;
  google_place_id: string | null;
  channel_whatsapp: boolean;
  channel_email: boolean;
  channel_booking: boolean;
  subscription_override: boolean;
  // Instagram OAuth
  instagram_connected: boolean;
  instagram_media_cache: InstagramMediaItem[] | null;
  instagram_cache_at: string | null;
  // v2 profile fields
  bio: string | null;
  years_experience: number | null;
  languages_spoken: string[] | null;
  specializations: string[] | null;
  certifications: Record<string, unknown>[] | null;
  awards_press: Record<string, unknown>[] | null;
  price_examples: Record<string, unknown>[] | null;
  portfolio_image_labels: Record<string, string> | null;
  opening_hours: Record<string, unknown> | null;
  deposit_mode: "none" | "percentage" | "fixed" | null;
  deposit_value: number | null;
  covers_up: boolean | null;
  artist_gender: string | null;
  facilities: string[] | null;
}

export interface PricingSettings {
  id: string;
  artist_id: string;
  minimum_price: number;
  hourly_rate: number;
  daily_rate: number | null;
  design_cost_type: "fixed" | "hourly";
  design_cost_amount: number;
  deposit_type: "percentage" | "fixed";
  deposit_amount: number;
  created_at: string;
  updated_at: string;
}

export interface SizeSetting {
  id: string;
  pricing_settings_id: string;
  size_category: SizeCategory;
  min_hours: number;
  max_hours: number;
  price_per_cm2: number | null;
}

export interface ComplexityMultiplier {
  id: string;
  pricing_settings_id: string;
  complexity_level: ComplexityLevel;
  multiplier: number;
}

export interface BodyPartMultiplier {
  id: string;
  pricing_settings_id: string;
  body_part: BodyPart;
  multiplier: number;
}

export interface StyleMultiplier {
  id: string;
  pricing_settings_id: string;
  tattoo_style: TattooStyle;
  multiplier: number;
  is_active: boolean;
}

export interface FullPricingConfig {
  artist: ArtistProfile;
  pricing: PricingSettings;
  sizeSettings: SizeSetting[];
  complexityMultipliers: ComplexityMultiplier[];
  bodyPartMultipliers: BodyPartMultiplier[];
  styleMultipliers: StyleMultiplier[];
}

export interface PriceCalculation {
  minPrice: number;
  maxPrice: number;
  breakdown: {
    basePrice: { min: number; max: number };
    timeLabor: { min: number; max: number };
    estimatedHours: { min: number; max: number };
    correctedHours: { min: number; max: number };
    subtotalBeforeMultipliers: { min: number; max: number };
    baseLaborRange: { min: number; max: number };
    styleSurchargeRange: { min: number; max: number };
    complexityFactor: number;
    bodyPartFactor: number;
    styleFactor: number;
    totalMultiplier: number;
    designCost: number;
    deposit: number;
    useDailyRate: boolean;
    dailyRate: number | null;
    sessions: number | null;
    sessionHours: number;
    colorSurcharge: number;
    depositPercentage: number | null;
  };
}

export const COMPLEXITY_LABELS: Record<ComplexityLevel, string> = {
  simple: "Simple",
  medium: "Medium",
  detailed: "Detailed",
};

export const STYLE_LABELS: Record<TattooStyle, string> = {
  black_grey: "Black & Grey",
  full_color: "Full Color",
  realism: "Realism",
  fine_line: "Fine Line / Minimal",
  traditional: "Traditional",
  neo_traditional: "Neo Traditional",
  watercolor: "Watercolor",
  tribal: "Tribal",
  japanese: "Japanese",
  geometric: "Geometric",
  lettering: "Lettering",
  script: "Script",
  dotwork: "Dotwork",
  mandala: "Mandala",
  blackwork: "Blackwork",
  illustrative: "Illustrative",
  surrealism: "Surrealism",
  ornamental: "Ornamental",
  micro_realism: "Micro Realism",
  trash_polka: "Trash Polka",
  other: "Other",
};

export const BODY_PART_LABELS: Record<BodyPart, string> = {
  arm: "Arm",
  forearm: "Forearm",
  upper_arm: "Upper Arm",
  leg: "Leg",
  thigh: "Thigh",
  calf: "Calf",
  back: "Back",
  chest: "Chest",
  ribs: "Ribs",
  stomach: "Stomach",
  hand: "Hand",
  foot: "Foot",
  neck: "Neck",
  face: "Face",
  shoulder: "Shoulder",
  hip: "Hip",
  finger: "Finger",
  other: "Other",
};

export const SIZE_LABELS: Record<SizeCategory, string> = {
  mini: "Mini (< 5 cm)",
  small: "Small (5-10 cm)",
  medium: "Medium (10-20 cm)",
  large: "Large (20-35 cm)",
  xl: "Extra Large (35+ cm)",
};

export const DEFAULT_SIZE_SETTINGS: Omit<SizeSetting, "id" | "pricing_settings_id">[] = [
  { size_category: "mini", min_hours: 0.5, max_hours: 1, price_per_cm2: null },
  { size_category: "small", min_hours: 1, max_hours: 2, price_per_cm2: null },
  { size_category: "medium", min_hours: 2, max_hours: 4, price_per_cm2: null },
  { size_category: "large", min_hours: 4, max_hours: 8, price_per_cm2: null },
  { size_category: "xl", min_hours: 8, max_hours: 16, price_per_cm2: null },
];

export const DEFAULT_COMPLEXITY_MULTIPLIERS: Omit<ComplexityMultiplier, "id" | "pricing_settings_id">[] = [
  { complexity_level: "simple", multiplier: 1.0 },
  { complexity_level: "medium", multiplier: 1.3 },
  { complexity_level: "detailed", multiplier: 1.6 },
];

export const DEFAULT_STYLE_MULTIPLIERS: Omit<StyleMultiplier, "id" | "pricing_settings_id">[] = [
  { tattoo_style: "black_grey", multiplier: 1.0, is_active: true },
  { tattoo_style: "full_color", multiplier: 1.2, is_active: true },
  { tattoo_style: "realism", multiplier: 1.5, is_active: true },
  { tattoo_style: "fine_line", multiplier: 1.0, is_active: true },
  { tattoo_style: "traditional", multiplier: 1.1, is_active: true },
  { tattoo_style: "neo_traditional", multiplier: 1.2, is_active: true },
  { tattoo_style: "watercolor", multiplier: 1.3, is_active: true },
  { tattoo_style: "tribal", multiplier: 1.0, is_active: true },
  { tattoo_style: "japanese", multiplier: 1.4, is_active: true },
  { tattoo_style: "geometric", multiplier: 1.1, is_active: true },
  { tattoo_style: "lettering", multiplier: 0.9, is_active: true },
  { tattoo_style: "script", multiplier: 0.9, is_active: true },
  { tattoo_style: "dotwork", multiplier: 1.3, is_active: true },
  { tattoo_style: "mandala", multiplier: 1.2, is_active: true },
  { tattoo_style: "blackwork", multiplier: 1.1, is_active: true },
  { tattoo_style: "illustrative", multiplier: 1.3, is_active: true },
  { tattoo_style: "surrealism", multiplier: 1.5, is_active: true },
  { tattoo_style: "ornamental", multiplier: 1.2, is_active: true },
  { tattoo_style: "micro_realism", multiplier: 1.6, is_active: true },
  { tattoo_style: "trash_polka", multiplier: 1.3, is_active: true },
  { tattoo_style: "other", multiplier: 1.0, is_active: true },
];

export const DEFAULT_BODY_PART_MULTIPLIERS: Omit<BodyPartMultiplier, "id" | "pricing_settings_id">[] = [
  { body_part: "arm", multiplier: 1.0 },
  { body_part: "forearm", multiplier: 1.0 },
  { body_part: "upper_arm", multiplier: 1.0 },
  { body_part: "leg", multiplier: 1.0 },
  { body_part: "thigh", multiplier: 1.0 },
  { body_part: "calf", multiplier: 1.0 },
  { body_part: "back", multiplier: 1.0 },
  { body_part: "chest", multiplier: 1.1 },
  { body_part: "ribs", multiplier: 1.4 },
  { body_part: "stomach", multiplier: 1.2 },
  { body_part: "hand", multiplier: 1.3 },
  { body_part: "foot", multiplier: 1.3 },
  { body_part: "neck", multiplier: 1.4 },
  { body_part: "face", multiplier: 1.5 },
  { body_part: "shoulder", multiplier: 1.0 },
  { body_part: "hip", multiplier: 1.1 },
  { body_part: "finger", multiplier: 1.3 },
  { body_part: "other", multiplier: 1.0 },
];
